import { useState, useRef, useEffect, useCallback } from "react";

export interface UseIntersectionObserverOptions {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
  observeFirstElement?: boolean;
}

export function useIntersectionObserver<T extends HTMLElement>(
  {
    root = null,
    rootMargin = "0px",
    threshold = 0,
    observeFirstElement = false,
  }: UseIntersectionObserverOptions = {},
  cb?: () => void | undefined,
) {
  const [ref, setRef] = useState<T | null>(null);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const currentEntry = useRef<IntersectionObserverEntry | undefined>(undefined);
  const prevEntry = useRef<IntersectionObserverEntry | undefined>(undefined);
  const hasIntersected = useRef<boolean>(false);
  let observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback<IntersectionObserverCallback>(
    (entries, observer) => {
      const targetEntries = observeFirstElement ? [entries[0]] : entries;

      targetEntries.forEach((entry) => {
        if (entry.isIntersecting && !hasIntersected.current) {
          setIsIntersecting(entry.isIntersecting);
          currentEntry.current = entry;
          hasIntersected.current = true;
          if (cb) cb();
        }

        if (!entry.isIntersecting && hasIntersected.current) {
          hasIntersected.current = false;
        }
      });
    },
    [observeFirstElement, cb],
  );

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      console.warn("Intersection Observer is not supported in this browser");
      return;
    }
    if (!ref) return;
    const observer = new IntersectionObserver(handleIntersection, {
      root,
      rootMargin,
      threshold,
    });

    observer.observe(ref);

    return () => {
      setIsIntersecting(false);
      observer.unobserve(ref);
      observer.disconnect();
    };
  }, [ref, root, rootMargin, threshold, handleIntersection]);

  useEffect(() => {
    if (prevEntry.current !== currentEntry.current) {
      prevEntry.current = currentEntry.current;
      setIsIntersecting(false);
      currentEntry.current = undefined;
    }
  }, [ref, currentEntry, isIntersecting]);
  return [setRef, isIntersecting, currentEntry.current] as const;
}

export default useIntersectionObserver;
